// Das Übungsufer (K46 bis K52): was über und unter den Figuren liegt. Hindernisse, Ansagen der Sturmwesen, Befehle, die
// Zielvorschau der Kniffe, die Mulde und der Schatten am Landepunkt. Bis der Grafik-Lead Bilder liefert (Auftrag in
// docs/uebergabe/an-grafik-lead-uebungsufer.md), zeichnet der Code Platzhalter; die Ansagen schlicht violett,
// halbdurchsichtig, mit einem Ring, der abläuft (Auftrag des Kampf-Designers). Steht ein Bild im Manifest, gilt das Bild.
import { getAbility, practice } from '../../content/index.ts';
import { FrameFlag, FrameFlag2, type BattleResult, type Obstacle, type Order, type TelegraphShape } from '../../sim/index.ts';
import { blit, drawStrip, fxFrame, image, pixelScale, pixelsPerUnit, sprite, sprites, stripFrame, type ArtSprite } from '../art.ts';
import { fellArt } from '../shore/scenery.ts';
import { figureGeo, pointOf } from '../figures.ts';
import type { DrawUnit, Prop } from '../render.ts';
import { byRadius, drawLoop, drawMarker, loopFrame } from './look.ts';

const VIOLET = '176, 124, 232';
const GOLD = '246, 199, 107';
const CREAM = '243, 233, 210';

// ---------- Hindernisse (Platzhalter) ----------

/** Der Schatten und der Körper eines Steins, in Gerätepixeln */
function stone(ctx: CanvasRenderingContext2D, s: number, o: Obstacle): void {
  const x = o.x * s;
  const y = o.y * s;
  const r = o.radius * s;
  ctx.save();
  ctx.fillStyle = 'rgba(10, 14, 12, 0.35)';
  ctx.beginPath();
  ctx.ellipse(x + r * 0.15, y + r * 0.35, r * 1.15, r * 0.55, 0, 0, Math.PI * 2);
  ctx.fill();
  const g = ctx.createLinearGradient(x - r, y - r * 1.4, x + r, y + r * 0.4);
  g.addColorStop(0, '#9ea39a');
  g.addColorStop(1, '#5d625c');
  ctx.fillStyle = g;
  ctx.strokeStyle = '#3c403b';
  ctx.lineWidth = Math.max(1, s * 0.35);
  ctx.beginPath();
  ctx.moveTo(x - r * 1.05, y + r * 0.25);
  ctx.bezierCurveTo(x - r * 1.15, y - r * 0.9, x - r * 0.2, y - r * 1.45, x + r * 0.45, y - r * 1.05);
  ctx.bezierCurveTo(x + r * 1.1, y - r * 0.7, x + r * 1.15, y + r * 0.1, x + r * 0.85, y + r * 0.35);
  ctx.quadraticCurveTo(x, y + r * 0.65, x - r * 1.05, y + r * 0.25);
  ctx.fill();
  ctx.stroke();
  // Moos und Glanz, damit er nach Herbstufer aussieht
  ctx.fillStyle = 'rgba(122, 150, 84, 0.75)';
  ctx.beginPath();
  ctx.ellipse(x - r * 0.35, y - r * 0.95, r * 0.4, r * 0.18, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 240, 0.35)';
  ctx.lineWidth = Math.max(1, s * 0.3);
  ctx.beginPath();
  ctx.arc(x + r * 0.15, y - r * 0.55, r * 0.45, -2.6, -1.6);
  ctx.stroke();
  ctx.restore();
}

function log(ctx: CanvasRenderingContext2D, s: number, o: Obstacle): void {
  const x1 = o.x * s;
  const y1 = o.y * s;
  const x2 = (o.x2 ?? o.x) * s;
  const y2 = (o.y2 ?? o.y) * s;
  const r = o.radius * s;
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const len = Math.hypot(x2 - x1, y2 - y1);
  ctx.save();
  ctx.translate(x1, y1);
  ctx.rotate(ang);
  ctx.fillStyle = 'rgba(10, 14, 12, 0.35)';
  ctx.beginPath();
  ctx.ellipse(len / 2, r * 0.9, len / 2 + r, r * 0.6, 0, 0, Math.PI * 2);
  ctx.fill();
  const g = ctx.createLinearGradient(0, -r, 0, r);
  g.addColorStop(0, '#9b7655');
  g.addColorStop(1, '#5e4330');
  ctx.fillStyle = g;
  ctx.strokeStyle = '#3d2b1f';
  ctx.lineWidth = Math.max(1, s * 0.35);
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(len, -r);
  ctx.arc(len, 0, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(0, r);
  ctx.arc(0, 0, r, Math.PI / 2, -Math.PI / 2);
  ctx.fill();
  ctx.stroke();
  // Rinde und Schnittfläche mit Jahresringen
  ctx.strokeStyle = 'rgba(40, 26, 16, 0.55)';
  ctx.lineWidth = Math.max(1, s * 0.25);
  for (let i = 1; i < 5; i++) {
    const px = (len * i) / 5;
    ctx.beginPath();
    ctx.moveTo(px - r * 0.3, -r * 0.6);
    ctx.quadraticCurveTo(px, 0, px - r * 0.2, r * 0.6);
    ctx.stroke();
  }
  ctx.fillStyle = '#c9a57a';
  ctx.beginPath();
  ctx.ellipse(len, 0, r * 0.55, r * 0.95, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(110, 76, 48, 0.8)';
  ctx.beginPath();
  ctx.ellipse(len, 0, r * 0.28, r * 0.5, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

const BUSH_LEAVES = [
  [-0.75, -0.25, 0.45],
  [-0.35, -0.55, 0.5],
  [0.15, -0.6, 0.52],
  [0.6, -0.3, 0.48],
  [0.85, 0.05, 0.36],
  [-0.95, 0.1, 0.34],
];

/** Gebüsch, hinterer Teil: unter den Figuren */
function bushBack(ctx: CanvasRenderingContext2D, s: number, o: Obstacle, T: number): void {
  const x = o.x * s;
  const y = o.y * s;
  const r = o.radius * s;
  ctx.save();
  ctx.fillStyle = 'rgba(10, 14, 12, 0.3)';
  ctx.beginPath();
  ctx.ellipse(x, y + r * 0.25, r * 1.15, r * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#3f5a34';
  for (const [dx, dy, rr] of BUSH_LEAVES) {
    ctx.beginPath();
    ctx.arc(x + dx! * r, y + dy! * r - r * 0.35 + Math.sin(T * 1.3 + dx! * 3) * s * 0.15, rr! * r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Gebüsch, vorderer Teil: über den Figuren, die darin stehen (wie das Laub vor dem Eichhörnchen) */
function bushFront(ctx: CanvasRenderingContext2D, s: number, o: Obstacle, T: number): void {
  const x = o.x * s;
  const y = o.y * s;
  const r = o.radius * s;
  ctx.save();
  const leaves = [
    [-0.7, 0.05, 0.42, '#6a8a43'],
    [-0.15, 0.15, 0.48, '#7b9a4a'],
    [0.45, 0.08, 0.44, '#6a8a43'],
    [0.95, 0.22, 0.3, '#8a6a3a'],
    [-1.0, 0.25, 0.28, '#a0602e'],
  ] as const;
  for (const [dx, dy, rr, c] of leaves) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.arc(x + dx * r, y + dy * r + Math.sin(T * 1.7 + dx * 2) * s * 0.12, rr * r, 0, Math.PI * 2);
    ctx.fill();
  }
  // ein paar Beeren und Herbstblätter
  ctx.fillStyle = '#c8463a';
  for (const [dx, dy] of [[-0.3, 0.05], [0.25, 0.25], [0.6, -0.05]] as const) {
    ctx.beginPath();
    ctx.arc(x + dx * r, y + dy * r, s * 0.45, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** Das Bild des Grafik-Leads zu einem Hindernis (practice.json, Feld art), sobald es im Manifest steht */
const OBSTACLE_ART = new Map(practice.obstacles.filter((o) => o.art).map((o) => [o.id, o.art!]));
const artOf = (o: Obstacle): string | null => {
  const id = OBSTACLE_ART.get(o.id);
  return id && sprite('marker', id) && image(sprite('marker', id)!.file) ? id : null;
};
/** Wo das Bild eines Hindernisses steht: Mitte des Steins und des Gebüschs, Mitte des Stamms */
const footOf = (o: Obstacle) => ({ x: (o.x + (o.x2 ?? o.x)) / 2, y: (o.y + (o.y2 ?? o.y)) / 2 });

/**
 * K49: Hindernisse zwischen den Figuren, nach der Tiefe: Wer weiter hinten steht als die Mitte eines Steins oder des
 * Stamms, den verdeckt er. Beim Gebüsch wie beim Baum des Eichhörnchens: erst gebuesch, dann wer darin steht, dann
 * gebuesch-vorn; wer mehr als 6 Bildpixel vor dem Anker steht, kommt danach (Grafik-Lead, Version 7).
 */
export function obstacleProps(ctx: CanvasRenderingContext2D, s: number, obstacles: Obstacle[], T: number): Prop[] {
  const out: Prop[] = [];
  for (const o of obstacles) {
    const art = artOf(o);
    const f = footOf(o);
    if (o.kind === 'bush') {
      out.push({ y: o.y - o.radius, draw: () => (art ? drawMarker(ctx, s, art, f.x, f.y, T) : bushBack(ctx, s, o, T)) });
      out.push({ y: o.y + 6 / pixelsPerUnit(), draw: () => (art ? drawMarker(ctx, s, `${art}-vorn`, f.x, f.y, T) : bushFront(ctx, s, o, T)) });
      continue;
    }
    out.push({ y: f.y, draw: () => (art ? drawMarker(ctx, s, art, f.x, f.y, T) : o.kind === 'log' ? log(ctx, s, o) : stone(ctx, s, o)) });
  }
  return out;
}

/**
 * Wo etwas liegt: ground am Boden, unter den Figuren (nach dem Abdunkeln der Pause, darum hell), top über allem. Ringe
 * mit splitRow (Version 7, wie bedroht-<rx>) teilen sich: Der hintere Teil liegt unter der Figur, der Rest darüber; so
 * geht nichts durch eine Figur hindurch (Fabi, 19.10.2026).
 */
export type Layer = 'ground' | 'top';

/** Ein Bild am Boden (Feldeinheiten), mit splitRow geteilt; ohne splitRow ganz in der Ebene whole */
function ringArt(ctx: CanvasRenderingContext2D, s: number, sp: ArtSprite, frame: number, x: number, y: number, layer: Layer, alpha = 1, whole: Layer = 'top'): void {
  const img = image(sp.file);
  if (!img) return;
  const k = pixelScale(s);
  const split = sp.splitRow;
  if (split === undefined || !sp.frameSize) {
    if (layer === whole) drawStrip(ctx, sp.kind, sp.id, frame, x * s, y * s, k, { alpha });
    return;
  }
  const [fw, fh] = sp.frameSize;
  const c = sp.center ?? [Math.floor(fw / 2), Math.floor(fh / 2)];
  const [y0, h] = layer === 'ground' ? [0, split] : [split, fh - split];
  if (h <= 0) return;
  ctx.save();
  ctx.globalAlpha *= alpha;
  blit(ctx, img, frame * fw, y0, fw, h, x * s, y * s, [c[0], c[1] - 0.5 - y0], k);
  ctx.restore();
}

/** Eine Schleife (mit Bild) am Boden, geteilt; false, wenn es das Bild nicht gibt */
function ringLoop(ctx: CanvasRenderingContext2D, s: number, sp: ArtSprite | undefined, x: number, y: number, t: number, layer: Layer, alpha = 1, whole: Layer = 'top'): boolean {
  if (!sp || !image(sp.file)) return false;
  ringArt(ctx, s, sp, loopFrame(sp, t), x, y, layer, alpha, whole);
  return true;
}

/** Über dem Kopf, über der ruhigen Leiste (Gerätepixel): für die Uhr der Ansagen */
function aboveBar(u: DrawUnit, s: number): number {
  const g = figureGeo(u, s);
  if (!g) return (u.y - u.radius * 2.6 - 3) * s;
  const head = pointOf(g, 'kopf')?.y ?? g.top;
  // Leiste: 3 Bildpixel über dem Kopf, 6 hoch; die Uhr (13 hoch) mit 2 Pixeln Luft darüber
  return head - 18 * g.k;
}

// ---------- Ansagen (K48) ----------

/** Der ablaufende Ring: voll bei der Ansage, leer beim Treffer */
function countdown(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, left: number, s: number, alpha: number): void {
  // Mit Bild: ansage-uhr, Bild 0 voll, das letzte leer
  const clock = sprite('marker', 'ansage-uhr');
  if (clock) {
    const n = Math.max(1, clock.frames ?? 1);
    if (drawStrip(ctx, 'marker', 'ansage-uhr', Math.min(n - 1, Math.floor((1 - Math.max(0, left)) * n)), x, y, pixelScale(s), { alpha })) return;
  }
  ctx.save();
  ctx.lineWidth = Math.max(2, s * 0.55);
  ctx.strokeStyle = `rgba(${VIOLET}, ${0.25 * alpha})`;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = `rgba(${VIOLET}, ${0.95 * alpha})`;
  ctx.beginPath();
  ctx.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0, left));
  ctx.stroke();
  ctx.restore();
}

export type ActiveTelegraph = { id: number; unit: number; kniff: string; t: number; at: number; shape: TelegraphShape };

/** Welche Ansagen zur Zeit T laufen */
export function telegraphsAt(r: BattleResult, T: number): ActiveTelegraph[] {
  const open = new Map<number, ActiveTelegraph>();
  for (const e of r.events) {
    if (e.t > T + 1e-9) break;
    if (e.type === 'telegraph') open.set(e.id, { id: e.id, unit: e.unit, kniff: e.kniff, t: e.t, at: e.at, shape: e.shape });
    else if (e.type === 'telegraphEnd') open.delete(e.id);
  }
  return [...open.values()].filter((x) => x.at > T - 1e-9);
}

/** Version 8: Kniffe, deren Ansage einen Sprung zeigt (Sprungspur vom Springer zum Ziel) */
const LEAPS = new Set(['sprung', 'doppelsprung', 'blattschwert']);

/** Version 8: das Bild einer Fläche oder eines Rings am Boden, nach Form und Größe (Feld shape, radius) oder Kniff */
function areaArt(shape: 'area' | 'ring', radius: number, kniff: string): ArtSprite | undefined {
  const own = sprites().filter((sp) => sp.kind === 'marker' && sp.shape === shape && (sp.kniff ?? sp.kniffNeu) === kniff);
  if (own.length) return own[0];
  return sprites().find((sp) => sp.kind === 'marker' && sp.shape === 'area' && sp.radius !== undefined && Math.abs(sp.radius - radius) < 0.01);
}

/**
 * Version 8: das Zeichen der Ansage über dem Ausführenden (ansage-zeichen-<kniff>, taucht auf, leuchtet, bleibt). Mit Uhr
 * dort stehen beide nebeneinander: Zeichen links (Mitte 8 Bildpixel links), Uhr rechts (7 Bildpixel rechts der Kopfmitte)
 */
function signAndClock(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, tg: ActiveTelegraph, T: number, left: number | null, alpha: number): void {
  const sp = sprite('marker', `ansage-zeichen-${tg.kniff}`);
  const x = u.x * s;
  const y = aboveBar(u, s);
  const k = pixelScale(s);
  const frame = sp ? fxFrame({ ...sp, hold: true }, T - tg.t) : null;
  const signed = !!sp && frame !== null && !!image(sp.file);
  if (left === null) {
    if (signed) drawStrip(ctx, 'marker', sp!.id, frame!, x, y, k, { alpha });
    return;
  }
  if (signed) drawStrip(ctx, 'marker', sp!.id, frame!, x - 8 * k, y, k, { alpha });
  countdown(ctx, signed ? x + 7 * k : x, y, s * 1.6, left, s, alpha);
}

/**
 * Version 8: die Sprungspur, Staubpunkte auf dem Bogen vom Springer zum Ziel (Scheitel 14 Einheiten über dem höheren Ende),
 * etwa alle 5 Feldeinheiten einer; sie wandern zum Ziel und werden größer. Ohne Bild die gestrichelte Linie
 */
function leapTrail(ctx: CanvasRenderingContext2D, s: number, from: DrawUnit, to: DrawUnit, T: number, alpha: number): void {
  const cx = (from.x + to.x) / 2;
  const cy = Math.min(from.y, to.y) - 14;
  const at = (q: number) => ({ x: (1 - q) ** 2 * from.x + 2 * (1 - q) * q * cx + q * q * to.x, y: (1 - q) ** 2 * from.y + 2 * (1 - q) * q * cy + q * q * to.y });
  const sp = sprite('marker', 'ansage-sprungspur');
  if (!sp || !image(sp.file)) {
    ctx.save();
    ctx.strokeStyle = `rgba(${VIOLET}, ${0.45 * alpha})`;
    ctx.lineWidth = Math.max(1.5, s * 0.3);
    ctx.setLineDash([s * 0.8, s * 1.2]);
    ctx.beginPath();
    ctx.moveTo(from.x * s, from.y * s);
    ctx.quadraticCurveTo(cx * s, cy * s, to.x * s, to.y * s);
    ctx.stroke();
    ctx.restore();
    return;
  }
  let len = 0;
  let prev = at(0);
  for (let i = 1; i <= 20; i++) {
    const p = at(i / 20);
    len += Math.hypot(p.x - prev.x, p.y - prev.y);
    prev = p;
  }
  const n = Math.max(2, Math.round(len / 5));
  const phase = (T * 0.8) % 1;
  for (let i = 0; i < n; i++) {
    const q = (i + phase) / n;
    if (q > 1) continue;
    const p = at(q);
    drawStrip(ctx, 'marker', 'ansage-sprungspur', Math.min(3, Math.floor(q * 4)), p.x * s, p.y * s, pixelScale(s), { alpha: alpha * Math.min(1, 5 * q, 5 * (1 - q)) });
  }
}

/** Streifen, Marken, Flächen und Ringe am Boden (ground), die Uhren, Zeichen und die Sprungspur darüber (top) */
export function drawTelegraphs(ctx: CanvasRenderingContext2D, s: number, list: ActiveTelegraph[], units: DrawUnit[], T: number, bright: boolean, layer: Layer): void {
  const byUid = new Map(units.map((u) => [u.uid, u]));
  const alpha = bright ? 1 : 0.8;
  for (const tg of list) {
    const k = Math.max(0, Math.min(1, (T - tg.t) / Math.max(1e-6, tg.at - tg.t)));
    const sh = tg.shape;
    const from = byUid.get(tg.unit);
    ctx.save();
    // S13 (Erste Fassung): Ansagen eigener Kniffe (Baum fällen des Bibers) in Gold statt Violett
    const col = from?.side === 'friend' ? GOLD : VIOLET;
    if (sh.kind === 'stripe') {
      if (layer === 'top') {
        countdown(ctx, (sh.x + sh.dx * sh.length) * s, (sh.y + sh.dy * sh.length) * s, s * 2.2, 1 - k, s, alpha);
        if (from) signAndClock(ctx, s, from, tg, T, null, alpha);
        ctx.restore();
        continue;
      }
      // Rammstoß: ein Streifen am Boden in Stoßrichtung, der bis zum Treffer zuwächst
      ctx.translate(sh.x * s, sh.y * s);
      ctx.rotate(Math.atan2(sh.dy, sh.dx));
      const L = sh.length * s;
      const W = sh.width * s;
      ctx.fillStyle = `rgba(${col}, ${0.12 * alpha})`;
      ctx.fillRect(0, -W / 2, L, W);
      ctx.fillStyle = `rgba(${col}, ${(0.22 + 0.25 * k) * alpha})`;
      ctx.fillRect(0, -W / 2, L * k, W);
      ctx.strokeStyle = `rgba(${col}, ${0.9 * alpha})`;
      ctx.lineWidth = Math.max(1.5, s * 0.35);
      ctx.setLineDash([s * 1.4, s * 1]);
      ctx.strokeRect(0, -W / 2, L, W);
      ctx.setLineDash([]);
      // Pfeilspitzen in Stoßrichtung; mit Bild (ansage-pfeil, ein Bild je Richtung) nach dem Drehen, siehe unten
      const arrows = tg.kniff === 'baum-faellen' ? null : sprite('marker', 'ansage-pfeil');
      ctx.fillStyle = `rgba(${col}, ${0.8 * alpha})`;
      for (let i = 1; i <= 3 && !arrows && tg.kniff !== 'baum-faellen'; i++) {
        const px = (L * i) / 4;
        ctx.beginPath();
        ctx.moveTo(px + W * 0.18, 0);
        ctx.lineTo(px - W * 0.12, -W * 0.22);
        ctx.lineTo(px - W * 0.12, W * 0.22);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
      if (tg.kniff === 'baum-faellen') {
        // S13 (Runde 7): Der Schatten des Baums im Streifen wird dunkler, je näher der Stamm kommt
        const art = fellArt('marker', 'baum-faellt-schatten', sh.dx, sh.dy);
        const img = art ? image(art.sp.file) : null;
        if (art && img) {
          const [fw, fh] = art.sp.frameSize ?? [img.naturalWidth, img.naturalHeight];
          const c = art.sp.center ?? [0, 0];
          ctx.save();
          ctx.globalAlpha *= alpha;
          blit(ctx, img, Math.min(3, Math.floor(k * 4)) * fw, 0, fw, fh, sh.x * s, sh.y * s, [c[0], c[1] - 0.5], pixelScale(s), art.flip);
          ctx.restore();
        }
      }
      if (arrows) {
        // Richtung wie bei bedroht-<rx>: 0 nach rechts, im Uhrzeigersinn, so viele Schritte wie angles
        const dirs = arrows.angles?.length ?? arrows.frames ?? 1;
        const deg = ((Math.atan2(sh.dy, sh.dx) * 180) / Math.PI + 360) % 360;
        const f = Math.round((deg / 360) * dirs) % dirs;
        for (let i = 1; i <= 3; i++) {
          const q = (sh.length * i) / 4;
          drawStrip(ctx, 'marker', 'ansage-pfeil', f, (sh.x + sh.dx * q) * s, (sh.y + sh.dy * q) * s, pixelScale(s), { alpha: alpha * (i / 4 <= k + 0.25 ? 1 : 0.5) });
        }
      }
      continue;
    }
    if (sh.kind === 'mark') {
      // Windschnitt, Messerhieb, Blattschwert: eine Marke am Freund, auf den er springt oder zielt
      const u = byUid.get(sh.target);
      if (u) {
        const x = u.x * s;
        const y = u.y * s;
        const rr = (u.radius + 2.2 - 1.2 * k) * s;
        const art = byRadius('marker', 'ansage-ziel-', u.radius);
        const drawn = ringLoop(ctx, s, art, u.x, u.y, T, layer, alpha);
        if (layer === 'top') {
          if (!drawn) {
            ctx.strokeStyle = `rgba(${VIOLET}, ${0.95 * alpha})`;
            ctx.lineWidth = Math.max(1.5, s * 0.4);
            for (let i = 0; i < 4; i++) {
              const a = (i * Math.PI) / 2 + Math.PI / 4;
              ctx.beginPath();
              ctx.moveTo(x + Math.cos(a) * rr * 1.25, y + Math.sin(a) * rr * 0.65);
              ctx.lineTo(x + Math.cos(a) * rr * 1.65, y + Math.sin(a) * rr * 0.85);
              ctx.stroke();
            }
          }
          countdown(ctx, x, aboveBar(u, s), s * 1.6, 1 - k, s, alpha);
          if (from && LEAPS.has(tg.kniff)) leapTrail(ctx, s, from, u, T, alpha);
          if (from) signAndClock(ctx, s, from, tg, T, null, alpha);
        } else if (!drawn) {
          ctx.fillStyle = `rgba(${VIOLET}, ${(0.15 + 0.25 * k) * alpha})`;
          ctx.beginPath();
          ctx.ellipse(x, y, rr * 1.2, rr * 0.6, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
      continue;
    }
    if (sh.kind === 'area' || sh.kind === 'ring') {
      // K53 (Erste Fassung): Auf die Augen (Fläche um das Ziel), Grollen (um den Donnergroller), Hagelschauer und
      // Blitzschnell (Ring um den Ausführenden). Version 8: Bilder am Boden; die Uhr über dem Ziel, das Zeichen über dem
      // Ausführenden (am Ausführenden beide nebeneinander)
      const c = byUid.get(sh.kind === 'area' ? sh.target : tg.unit);
      if (c) {
        const x = c.x * s;
        const y = c.y * s;
        const R = sh.radius * s;
        const art = tg.kniff === 'hagelschauer' ? sprite('marker', 'ansage-hagel') : areaArt(sh.kind, sh.radius, tg.kniff);
        if (layer === 'top') {
          if (sh.kind === 'area') {
            countdown(ctx, x, aboveBar(c, s), s * 1.6, 1 - k, s, alpha);
            if (from) signAndClock(ctx, s, from, tg, T, null, alpha);
          } else signAndClock(ctx, s, c, tg, T, 1 - k, alpha);
        } else if (!ringLoop(ctx, s, art, c.x, c.y, T, 'ground', alpha, 'ground')) {
          if (sh.kind === 'ring' && tg.kniff === 'hagelschauer') {
            ctx.strokeStyle = `rgba(${VIOLET}, ${(0.4 + 0.5 * k) * alpha})`;
            ctx.lineWidth = Math.max(1.5, s * 0.5);
            ctx.beginPath();
            ctx.ellipse(x, y, (sh.radius + 6 * (1 - k)) * s * 1.2, (sh.radius + 6 * (1 - k)) * s * 0.6, 0, 0, Math.PI * 2);
            ctx.stroke();
          } else {
            // Ohne Bild in dieser Größe: der Kreis, den es genau trifft (wie die Vorwarnung des Blitzes)
            ctx.fillStyle = `rgba(${VIOLET}, ${(0.08 + 0.2 * k) * alpha})`;
            ctx.beginPath();
            ctx.arc(x, y, R, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = `rgba(${VIOLET}, ${0.9 * alpha})`;
            ctx.lineWidth = Math.max(1.5, s * 0.4);
            ctx.setLineDash([s * 1.4, s * 1]);
            ctx.lineDashOffset = -T * s * 4;
            ctx.beginPath();
            ctx.arc(x, y, R, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      }
      ctx.restore();
      continue;
    }
    ctx.restore();
  }
}

// ---------- Befehle (K46) ----------

/**
 * Befehle: der Weg vom Freund zum Ziel und die Marke am Zielort. Mit Bildern liegen Pfotenspur, Zweig und der hintere Teil
 * des Angriffsrings am Boden (ground), der vordere Teil darüber; ohne Bilder eine gestrichelte Linie wie bei der Leibwache
 */
export function drawOrders(ctx: CanvasRenderingContext2D, s: number, orders: Map<number, Order>, units: DrawUnit[], bright: boolean, selected: number | null, T: number, layer: Layer): void {
  const byUid = new Map(units.map((u) => [u.uid, u]));
  const trail = sprite('marker', 'befehl-spur');
  const trailOn = !!trail && !!image(trail.file);
  const flag = sprite('marker', 'befehl-ziel');
  const flagOn = !!flag && !!image(flag.file);
  for (const [uid, o] of orders) {
    const f = byUid.get(uid);
    if (!f || (f.flags & FrameFlag.alive) === 0) continue;
    const a = bright ? (uid === selected ? 1 : 0.85) : uid === selected ? 0.6 : 0.35;
    let to: { x: number; y: number } | null = null;
    let enemy: DrawUnit | undefined;
    if (o.kind === 'move' || (o.kind === 'kniff' && o.x !== undefined && o.y !== undefined)) to = { x: o.x!, y: o.y! };
    else if (o.kind === 'attack' || (o.kind === 'kniff' && o.target !== undefined)) {
      enemy = byUid.get(o.kind === 'attack' ? o.target : o.target!);
      if (enemy) to = { x: enemy.x, y: enemy.y };
    }
    if (!to) continue;
    ctx.save();
    ctx.strokeStyle = `rgba(${GOLD}, ${a})`;
    ctx.lineWidth = Math.max(1.5, s * 0.32);
    const d = Math.hypot(to.x - f.x, to.y - f.y);
    if (trailOn && layer === 'ground') {
      // Pfotenspur am Boden, alle drei Feldeinheiten ein Abdruck, links und rechts im Wechsel; vor dem Ziel bleibt Platz
      const stop = d - (enemy ? enemy.radius + 1.5 : 2);
      for (let q = f.radius + 1, i = 0; q < stop; q += 3, i++) drawMarker(ctx, s, 'befehl-spur', f.x + ((to.x - f.x) * q) / d, f.y + ((to.y - f.y) * q) / d, 0, { alpha: a, frame: i % Math.max(1, trail!.frames ?? 1) });
    } else if (!trailOn && layer === 'top') {
      ctx.setLineDash([s * 1.2, s * 0.9]);
      ctx.beginPath();
      ctx.moveTo(f.x * s, f.y * s);
      ctx.lineTo(to.x * s, to.y * s);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    if (o.kind === 'move' && flagOn) {
      // Ein Zweiglein mit goldenem Blatt, in die Erde gesteckt
      if (layer === 'ground') drawMarker(ctx, s, 'befehl-ziel', to.x, to.y, T, { alpha: a });
    } else if (o.kind === 'move' && layer === 'top') {
      // Marke am Boden: ein kleiner Ring mit Kreuz
      ctx.fillStyle = `rgba(${GOLD}, ${a * 0.25})`;
      ctx.beginPath();
      ctx.ellipse(to.x * s, to.y * s, s * 2.2, s * 1.1, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo((to.x - 1) * s, to.y * s);
      ctx.lineTo((to.x + 1) * s, to.y * s);
      ctx.moveTo(to.x * s, (to.y - 0.6) * s);
      ctx.lineTo(to.x * s, (to.y + 0.6) * s);
      ctx.stroke();
    } else if (enemy && !ringLoop(ctx, s, byRadius('marker', 'befehl-angriff-', enemy.radius), enemy.x, enemy.y, T, layer, a) && layer === 'top') {
      // Angriff ohne Bild: ein Ring um das Ziel
      const rx = (enemy.radius + 1.6) * s;
      ctx.lineWidth = Math.max(2, s * 0.45);
      ctx.strokeStyle = `rgba(255, 150, 110, ${a})`;
      ctx.beginPath();
      ctx.ellipse(enemy.x * s, enemy.y * s, rx * 1.2, rx * 0.6, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }
}

/** Auswahl, Maus darüber und „Kniff bereit“: Ringe am Boden, mit Bildern hinten unter der Figur */
export function drawRings(ctx: CanvasRenderingContext2D, s: number, units: DrawUnit[], selected: number | null, hover: number | null, T: number, readyOf: (uid: number) => boolean, layer: Layer): void {
  for (const u of units) {
    if (u.side !== 'friend' || u.kind === 'tree' || (u.flags & FrameFlag.alive) === 0) continue;
    const x = u.x * s;
    const y = u.y * s;
    const rx = (u.radius + 1.6) * s;
    ctx.save();
    const ready = readyOf(u.uid);
    if (ready && !ringLoop(ctx, s, byRadius('marker', 'kniff-bereit-', u.radius), u.x, u.y, T, layer) && layer === 'top') {
      // K46: Kniff bereit, ein goldener Ring, der sacht pulsiert und wartet
      const p = 0.5 + 0.5 * Math.sin(T * 5 + u.uid);
      ctx.strokeStyle = `rgba(${GOLD}, ${0.55 + 0.4 * p})`;
      ctx.lineWidth = Math.max(2, s * (0.45 + 0.25 * p));
      ctx.shadowColor = `rgba(${GOLD}, 0.8)`;
      ctx.shadowBlur = s * 1.5;
      ctx.beginPath();
      ctx.ellipse(x, y, rx * 1.25 + p * s * 0.4, rx * 0.62 + p * s * 0.2, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    const shown = u.uid === selected || u.uid === hover;
    const pick = shown ? byRadius('marker', 'auswahl-', u.radius) : undefined;
    if (pick && image(pick.file)) {
      // Bild 0 gewählt, Bild 1 nur gezeigt
      const n = Math.max(1, pick.frames ?? 1);
      ringArt(ctx, s, pick, u.uid === selected ? 0 : Math.min(1, n - 1), u.x, u.y, layer, u.uid === selected ? 1 : 0.75);
    } else if (shown && layer === 'top') {
      ctx.shadowBlur = 0;
      ctx.strokeStyle = u.uid === selected ? `rgba(${CREAM}, 0.95)` : `rgba(${CREAM}, 0.5)`;
      ctx.lineWidth = Math.max(1.5, s * 0.35);
      ctx.setLineDash(u.uid === selected ? [] : [s, s * 0.7]);
      ctx.beginPath();
      ctx.ellipse(x, y, rx * 1.45, rx * 0.72, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  }
}

// ---------- Mulde, Herbeiwinken, Landepunkt ----------

/** K50: Wer in die Mulde schlittert, zieht Staub hinter sich her; wer feststeckt, steckt im Matsch */
export function drawGroundSigns(ctx: CanvasRenderingContext2D, s: number, r: BattleResult, units: DrawUnit[], T: number): void {
  const f = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.floor(T / 0.05)))];
  if (!f) return;
  const k = pixelScale(s);
  const before = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.floor(T / 0.05) - 3))];
  for (const u of units) {
    const fl2 = f.units[u.uid]?.[8] ?? 0;
    if ((u.flags & FrameFlag.alive) === 0) continue;
    if (fl2 & FrameFlag2.sliding) {
      // Mit Bild: rutschspur, nach rechts gezeichnet, für Rutschen nach links gespiegelt
      const left = (before?.units[u.uid]?.[0] ?? u.x) > u.x;
      if (!drawLoop(ctx, s, 'effect', 'rutschspur', u.x, u.y, T + u.uid, { alpha: 0.9, flip: left })) drawStrip(ctx, 'effect', 'staub', stripFrame('effect', 'staub', T + u.uid), u.x * s, u.y * s, k, { alpha: 0.8 });
    }
    if (fl2 & FrameFlag2.stuck && !(fl2 & FrameFlag2.sliding) && drawLoop(ctx, s, 'status', byRadius('status', 'steckt-fest-', u.radius)?.id ?? 'steckt-fest', u.x, u.y, T + u.uid)) continue;
    if (fl2 & FrameFlag2.stuck && !(fl2 & FrameFlag2.sliding)) {
      ctx.save();
      ctx.fillStyle = 'rgba(92, 66, 42, 0.75)';
      ctx.beginPath();
      ctx.ellipse(u.x * s, u.y * s, (u.radius + 1.2) * s * 1.1, (u.radius + 1.2) * s * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(140, 104, 70, 0.9)';
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 + u.uid;
        ctx.beginPath();
        ctx.arc((u.x + Math.cos(a) * (u.radius + 0.8)) * s, (u.y + Math.sin(a) * (u.radius + 0.8) * 0.45) * s, s * 0.55, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }
}

/** K50: Herbeigewinkt, eine goldene Spur zum Waschbär */
export function drawBeckon(ctx: CanvasRenderingContext2D, s: number, r: BattleResult, units: DrawUnit[], T: number): void {
  const f = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.floor(T / 0.05)))];
  if (!f) return;
  const coon = units.find((u) => u.art === 'waschbaer');
  if (!coon) return;
  for (const u of units) {
    const fl2 = f.units[u.uid]?.[8] ?? 0;
    if (!(fl2 & FrameFlag2.beckoned)) continue;
    ctx.save();
    ctx.strokeStyle = `rgba(${GOLD}, 0.7)`;
    ctx.lineWidth = Math.max(1.5, s * 0.4);
    ctx.setLineDash([s * 0.5, s * 0.8]);
    ctx.lineDashOffset = -T * s * 12;
    ctx.beginPath();
    ctx.moveTo(u.x * s, u.y * s);
    ctx.lineTo(coon.x * s, coon.y * s);
    ctx.stroke();
    ctx.restore();
  }
}

/** Bogenwürfe: Ein Schatten zeigt den Landepunkt und wächst, bis der Wurf landet (Vorschlag) */
export function drawLandingShadows(ctx: CanvasRenderingContext2D, s: number, r: BattleResult, T: number): void {
  const k = pixelScale(s);
  const ends = new Map<number, number>();
  for (const e of r.events) if (e.type === 'shotEnd') ends.set(e.id, e.t);
  for (const e of r.events) {
    if (e.t > T) break;
    if (e.type !== 'shot' || e.style !== 'arc') continue;
    const end = ends.get(e.id) ?? e.arrive;
    if (T > end) continue;
    const q = Math.max(0, Math.min(1, (T - e.t) / Math.max(1e-6, end - e.t)));
    const id = q < 0.45 ? 'schatten-klein' : q < 0.8 ? 'schatten-mittel' : 'schatten-gross';
    if (!drawStrip(ctx, 'effect', id, 0, e.tx * s, e.ty * s, k, { alpha: 0.35 + 0.45 * q })) {
      ctx.save();
      ctx.fillStyle = `rgba(10, 12, 20, ${0.2 + 0.35 * q})`;
      ctx.beginPath();
      ctx.ellipse(e.tx * s, e.ty * s, s * (1 + 2 * q), s * (0.5 + q), 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}

// ---------- Zielvorschau (K46: wie bei Into the Breach sieht man vor dem Klick, was der Zug bewirkt) ----------

export type Preview = {
  /** wer handelt */
  actor: DrawUnit;
  kniff: string;
  /** wohin der Zeiger zeigt (schon auf die Reichweite gekürzt) */
  at: { x: number; y: number };
  /** wer betroffen ist */
  hit: DrawUnit[];
  /** wer mitgeschützt wird (Bau graben) */
  guarded: DrawUnit[];
  /** das Ziel bei Kniffen auf eine Figur */
  target: DrawUnit | null;
  /** Ziele, die man wählen kann */
  choices: DrawUnit[];
};

export function drawPreview(ctx: CanvasRenderingContext2D, s: number, p: Preview, T: number): void {
  const k = getAbility(p.kniff);
  const m = k.mechanic;
  const ax = p.actor.x * s;
  const ay = p.actor.y * s;
  ctx.save();
  if (m === 'mulde' || m === 'ball') {
    const reach = k.params.reach!;
    // Reichweite um den Freund
    ctx.strokeStyle = `rgba(${CREAM}, 0.4)`;
    ctx.lineWidth = Math.max(1, s * 0.3);
    ctx.setLineDash([s * 1.2, s * 1.2]);
    ctx.beginPath();
    ctx.ellipse(ax, ay, reach * s, reach * s * 0.62, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Weg: unter der Erde (gepunktet) oder rollend (Strich mit Kugeln)
    ctx.strokeStyle = `rgba(${GOLD}, 0.9)`;
    ctx.lineWidth = Math.max(1.5, s * 0.4);
    ctx.setLineDash(m === 'mulde' ? [s * 0.4, s * 0.9] : [s * 1.6, s * 0.8]);
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(p.at.x * s, p.at.y * s);
    ctx.stroke();
    ctx.setLineDash([]);
    const R = (m === 'mulde' ? k.params.radius! : k.params.radius!) + p.actor.radius + 3;
    const color = m === 'mulde' ? '196, 150, 96' : GOLD;
    const pulse = 0.5 + 0.5 * Math.sin(T * 6);
    ctx.fillStyle = `rgba(${color}, ${0.12 + 0.06 * pulse})`;
    ctx.strokeStyle = `rgba(${color}, 0.95)`;
    ctx.lineWidth = Math.max(2, s * 0.45);
    ctx.beginPath();
    ctx.ellipse(p.at.x * s, p.at.y * s, R * s, R * s * 0.62, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    if (m === 'mulde') {
      // Wer rutscht: Pfeile zum Bau; wer mitgeschützt wird: ein goldener Ring
      for (const u of p.hit) {
        const dx = p.at.x - u.x;
        const dy = p.at.y - u.y;
        const d = Math.hypot(dx, dy) || 1;
        const stop = Math.max(0, d - p.actor.radius - u.radius - 0.4);
        const ex = u.x + (dx / d) * stop;
        const ey = u.y + (dy / d) * stop;
        arrow(ctx, s, u.x, u.y, ex, ey, `rgba(${VIOLET}, 0.95)`);
      }
      const gr = k.params.guardRadius!;
      ctx.strokeStyle = `rgba(${GOLD}, 0.55)`;
      ctx.setLineDash([s * 0.8, s * 0.8]);
      ctx.beginPath();
      ctx.ellipse(p.at.x * s, p.at.y * s, gr * s, gr * s * 0.62, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      for (const u of p.guarded) ring(ctx, s, u, `rgba(${GOLD}, 0.9)`, 1.1);
    }
    for (const u of p.hit) ring(ctx, s, u, m === 'mulde' ? `rgba(${VIOLET}, 0.95)` : 'rgba(255, 140, 100, 0.95)', 1.25);
    // Gezeichnete im Wirkkreis: Hier trifft der Kniff doppelt, und der Eisvogel stürzt hinterher
    if (m === 'ball') for (const u of p.hit) if (u.flags & FrameFlag.marked) label(ctx, s, u, '×2 und Sturzflug', '#ffd27a');
  } else if (m === 'beckon') {
    for (const u of p.choices) ring(ctx, s, u, `rgba(${GOLD}, ${u === p.target ? 0.95 : 0.4})`, u === p.target ? 1.4 : 1.1);
    if (p.target) {
      ctx.strokeStyle = `rgba(${GOLD}, 0.9)`;
      ctx.lineWidth = Math.max(1.5, s * 0.4);
      ctx.setLineDash([s * 0.6, s * 0.8]);
      ctx.beginPath();
      ctx.moveTo(p.target.x * s, p.target.y * s);
      ctx.quadraticCurveTo(((p.target.x + p.actor.x) / 2) * s, (Math.min(p.target.y, p.actor.y) - 8) * s, ax, ay);
      ctx.stroke();
      ctx.setLineDash([]);
      label(ctx, s, p.target, `kommt her, +${Math.round(k.params.heal! * practice.hpFactor)} Leben`, '#bdf0a8');
    }
  } else if (m === 'weakspot') {
    for (const u of p.choices) ring(ctx, s, u, `rgba(255, 120, 100, ${u === p.target ? 0.95 : 0.35})`, u === p.target ? 1.4 : 1.1);
    if (p.target) {
      const kk = pixelScale(s);
      drawStrip(ctx, 'status', 'schwachstelle-ziel', Math.floor(T * 6), p.target.x * s, (p.target.y - p.target.radius) * s, kk, { alpha: 0.85 });
      if (!sprite('status', 'schwachstelle-ziel')) ring(ctx, s, p.target, 'rgba(255, 90, 80, 1)', 0.8);
      label(ctx, s, p.target, 'Schwachstelle', '#ffb09a');
    }
  }
  ctx.restore();
}

function ring(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, color: string, grow: number): void {
  const rx = (u.radius + 1.4) * s * grow;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1.5, s * 0.45);
  ctx.beginPath();
  ctx.ellipse(u.x * s, u.y * s, rx * 1.2, rx * 0.6, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function arrow(ctx: CanvasRenderingContext2D, s: number, x0: number, y0: number, x1: number, y1: number, color: string): void {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const d = Math.hypot(dx, dy);
  if (d < 0.5) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1.5, s * 0.4);
  ctx.beginPath();
  ctx.moveTo(x0 * s, y0 * s);
  ctx.lineTo(x1 * s, y1 * s);
  ctx.stroke();
  const ux = dx / d;
  const uy = dy / d;
  ctx.beginPath();
  ctx.moveTo(x1 * s, y1 * s);
  ctx.lineTo((x1 - ux * 1.6 - uy * 0.9) * s, (y1 - uy * 1.6 + ux * 0.9) * s);
  ctx.lineTo((x1 - ux * 1.6 + uy * 0.9) * s, (y1 - uy * 1.6 - ux * 0.9) * s);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function label(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, text: string, color: string): void {
  ctx.save();
  ctx.font = `600 ${Math.max(11, s * 1.9)}px Zweigbuch, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.lineWidth = Math.max(2, s * 0.5);
  ctx.strokeStyle = 'rgba(20, 24, 22, 0.85)';
  const y = (u.y - u.radius * 2.6 - 3) * s;
  ctx.strokeText(text, u.x * s, y);
  ctx.fillStyle = color;
  ctx.fillText(text, u.x * s, y);
  ctx.restore();
}
